import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

export default class extends Controller {
  static values = { cookieName: String, resolveIntentUrl: String, steps: Object }
  static targets = [
    "readingTypeStep",
    "intentStep",
    "questionInput",
    "readingStyleStep",
    "readingStyleCard",
    "deckStep",
    "deckCard",
    "spreadStep",
    "spreadCard",
    "readingType",
    "intentId",
    "question",
    "readingStyleId",
    "deckId",
    "spreadId",
    "nextButton",
    "submitButton"]

  connect() {
    const saved = this.readState()

    if (saved.readingType) this.readingTypeTarget.value = saved.readingType

    const readingType = this.readingTypeTarget.value || "unspecified"
    this.currentSteps = this.stepsValue[readingType]
    this.currentStep = saved.step || this.currentSteps[0]

    this.hideAllSteps()
    this.showStepForCurrentStep()
    window.addEventListener("popstate", this.handlePopState)

    history.replaceState(
      { step: this.currentStep },
      "",
      window.location.pathname
    )

  }

  getStepTarget(stepName) {
    return {
      reading_type: this.readingTypeStepTarget,
      question: this.intentStepTarget,
      intent: this.intentStepTarget,
      reading_style: this.readingStyleStepTarget,
      deck: this.deckStepTarget,
      spread: this.spreadStepTarget
    }[stepName]
  }

  showStepForCurrentStep() {
    this.hideAllSteps()

    const target = this.getStepTarget(this.currentStep)
    if (target) this.showStep(target)
  }

  readState() {
    const match = document.cookie.match(new RegExp(this.cookieNameValue + "=([^;]+)"))
    if (!match) return {}
    try {
      return JSON.parse(decodeURIComponent(match[1]))
    } catch {
      return {}
    }
  }

  saveState(step) {
    const state = {
      step,
      readingType: this.readingTypeTarget.value,
      question: this.questionInputTarget.value,
      intentId: this.intentIdTarget.value,
      readingStyleId: this.readingStyleIdTarget.value,
      deckId: this.deckIdTarget.value,
      spreadId: this.spreadIdTarget.value
    }

    // Set cookie for 7 days
    const expires = new Date()
    expires.setDate(expires.getDate() + 7)

    document.cookie =
      this.cookieNameValue + "=" +
      encodeURIComponent(JSON.stringify(state)) +
      "; path=/; expires=" + expires.toUTCString()
  }

  hideAllSteps() {
    this.hideStep(this.readingTypeStepTarget)
    this.hideStep(this.intentStepTarget)
    this.hideStep(this.readingStyleStepTarget)
    this.hideStep(this.deckStepTarget)
    this.hideStep(this.spreadStepTarget)
    this.nextButtonTarget.classList.add('hidden')
  }

  updateUrl(stepName) {
    let basePath = "/readings/new"
    let newPath = stepName ? `${basePath}/${stepName}` : basePath
    //history.replaceState(null, "", newPath)

    history.pushState({ step: stepName }, "", newPath)
  }


  selectReadingType(event) {
    const readingType = event.currentTarget.dataset.readingType
    if (!readingType) return

    this.readingTypeTarget.value = readingType
    this.hideStep(this.readingTypeStepTarget)

    if (readingType === "specific") {
      this.showStep(this.intentStepTarget)
      this.updateUrl("intent")
      this.saveState("intent")
    } else {
      this.showStep(this.readingStyleStepTarget)
      this.updateUrl("reading_style")
      this.saveState("reading_style")
    }
  }

  prefillQuestion(event) {
    event.preventDefault()
    event.stopPropagation()

    const question = event.currentTarget.dataset.question
    this.questionInputTarget.value = question
    this.updateQuestion(question)
    this.toggleNextButton()
  }

  updateQuestion(question) {
    this.resolveIntent(question)
    this.questionTarget.value = question
    this.currentStep = 'intent'
    this.saveState("deck")
  }

  toggleNextButton() {
    const hasQuestion = this.questionInputTarget.value.trim().length > 0
    if (hasQuestion) {
      this.nextButtonTarget.classList.remove("hidden")
    } else {
      this.nextButtonTarget.classList.add("hidden")
    }
  }

  resolveIntent(question) {
    if (!question || question.trim().length < 5) return

    post(this.resolveIntentUrlValue, {
      body: JSON.stringify({ question }),
      contentType: "application/json",
      responseKind: "json"
    }).then(response => {
      if (!response.ok) return
      response.json.then(data => {
        if (!data.intent_id) return
        this.intentIdTarget.value = data.intent_id
        this.saveState("intent")
      })
    })
  }

  selectReadingStyle(event) {
    const slide = event.currentTarget
    const styleId = slide.dataset.readingStyleId
    if (!styleId) return

    this.readingStyleIdTarget.value = styleId

    this.readingStyleCardTargets.forEach(card => {
      card.classList.remove("ring-4", "ring-gold", "scale-105")
      card.classList.add("border-white", "rounded-md")
    })
    this.applySelectionEffect(slide)

    setTimeout(() => {
      this.hideStep(this.readingStyleStepTarget)
      this.showStep(this.deckStepTarget)
      this.currentStep = 'deck'
      this.updateUrl("deck")
    }, 250)

    this.saveState("deck")
  }

  selectDeck(event) {
    this.deckCardTargets.forEach(card => {
      card.classList.remove("ring-4", "ring-gold")
    })

    const card = event.currentTarget
    card.classList.add("ring-4", "ring-gold")

    this.deckIdTarget.value = card.dataset.deckId

    this.nextButtonTarget.classList.remove("hidden")
    this.nextButtonTarget.disabled = false
  }

  loadSpreadsForIntent() {
    const intentId = this.intentIdTarget.value
    const deckId = this.deckIdTarget.value
    const url = `/api/spreads?intent_id=${intentId}&deck_id=${deckId}`
    this.fetchSpreads(url)
  }

  loadSpreadsForReadingStyle() {
    const readingStyleId = this.readingStyleIdTarget.value
    const deckId = this.deckIdTarget.value
    const url = `/api/spreads?reading_style_id=${readingStyleId}&deck_id=${deckId}`
    this.fetchSpreads(url)
  }

  fetchSpreads(url) {
    // Show a loading state conceptually if needed, but not strictly required
    fetch(url, { headers: { Accept: "text/vnd.turbo-stream.html" } })
      .then(resp => {
        const count = parseInt(resp.headers.get("X-Spread-Count"), 10)

        if (count === 1) {
          const spreadId = resp.headers.get("X-Spread-Id")
          this.spreadIdTarget.value = spreadId

          this.element.requestSubmit()
          return null
        } else {
          const nextStepTarget = this.getStepTarget("spread")
          this.hideAllSteps()
          this.showStep(nextStepTarget)
          this.currentStep = "spread"
          this.saveState("spread")
          this.updateUrl("spread")

          return resp.text()
        }
      })
      .then(html => {
        if (html) {
          Turbo.renderStreamMessage(html)
        }
      })
  }

  nextStep(event) {
    event.preventDefault()

    const currentStep = this.currentStep
    const readingType = this.readingTypeTarget.value || "unspecified"
    const steps = this.stepsValue[readingType]
    const idx = steps.indexOf(currentStep)
    if (idx < 0 || idx >= (steps.length + 1)) return

    const nextStepName = steps[idx + 1]
    const nextStepTarget = this.getStepTarget(nextStepName)
    if (!nextStepTarget) return

    const currentStepTarget = this.getStepTarget(this.currentStep)

    if (nextStepName === 'spread') {
      if (this.readingStyleIdTarget && this.readingStyleIdTarget.value) {
        this.loadSpreadsForReadingStyle()
      } else if (this.intentIdTarget && this.intentIdTarget.value) {
        this.loadSpreadsForIntent()
      }
    } else {
      this.hideAllSteps()
      this.showStep(nextStepTarget)
      this.currentStep = nextStepName
      this.saveState(nextStepName)
      this.updateUrl(nextStepName)
    }
  }

  getNextStep(currentStep) {
    const readingType = this.readingTypeTarget.value || "unspecified"
    const steps = this.stepsValue[readingType]   // pick array from object
    const idx = steps.indexOf(currentStep)
    const nextStepName = idx >= 0 && idx < steps.length - 1 ? steps[idx + 1] : null
    return nextStepName
  }

  selectSpread(event) {
    this.spreadCardTargets.forEach(card => {
      card.classList.remove("ring-4", "ring-gold")
    })

    const card = event.currentTarget
    card.classList.add("ring-4", "ring-gold")

    this.spreadIdTarget.value = card.dataset.spreadId

    this.submitButtonTarget.classList.remove("hidden")
    this.updateUrl("spread")
  }

  hideStep(step) {
    step.classList.add(
      "opacity-0",
      "scale-95",
      "pointer-events-none",
      "absolute"
    )
    step.classList.remove(
      "opacity-100",
      "scale-100",
      "relative"
    )
  }

  showStep(step) {
    step.classList.remove(
      "opacity-0",
      "scale-95",
      "pointer-events-none",
      "absolute"
    )
    step.classList.add(
      "opacity-100",
      "scale-100",
      "relative"
    )
  }

  applySelectionEffect(element) {
    if (!element) return
    element.classList.add("ring-4", "ring-gold", "scale-105")
  }

  removeSelectionEffect(element) {
    if (!element) return
    element.classList.remove("ring-4", "ring-gold", "scale-105")
  }

  subscribe(event) {
    event.stopPropagation()
    event.preventDefault()

    const clickedCard = event.currentTarget

    this.spreadCardTargets.forEach(card => {
      card.classList.remove("ring-4", "ring-gold")
    })

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.classList.add("hidden")
    }

    const link = event.currentTarget.closest("a")
    if (link) {
      Turbo.visit(link.href, { frame: "modal" })
    }
  }

  subscribeDeck(event) {
    event.stopPropagation()
    event.preventDefault()

    this.deckCardTargets.forEach(card => {
      card.classList.remove("ring-4", "ring-gold")
    })

    this.nextButtonTarget.classList.add("hidden")

    const link = event.currentTarget.closest("a")
    if (link) {
      Turbo.visit(link.href, { frame: "modal" })
    }
  }

  handlePopState = (event) => {
    const step = event.state?.step
    if (!step) return

    this.currentStep = step
    this.showStepForCurrentStep()
  }

  disconnect() {
    window.removeEventListener("popstate", this.handlePopState)
  }
}
